import { X } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  matrixLocked: boolean;
  pinnedBlueprintVersion: number | null;
  currentBlueprintVersion: number | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  // "matrix_out_of_sync" (default): the assessment matrix is stale and will be
  // regenerated first. "manual_edits": a candidate's assessment was manually
  // edited and rescoring would overwrite it — confirm the overwrite.
  variant?: "matrix_out_of_sync" | "manual_edits";
}

// Shown before triggering an assessment-scores update either when the
// assessment matrix is out of date for the current role blueprint, or when a
// candidate's assessment was manually edited and a rescore would overwrite it.
// Makes the consequence explicit and reassures the user that the matrix's
// locked state will be preserved.
export function UpdateAssessmentScoresDialog({
  open,
  matrixLocked,
  pinnedBlueprintVersion,
  currentBlueprintVersion,
  onClose,
  onConfirm,
  variant = "matrix_out_of_sync",
}: Props) {
  const manualEdits = variant === "manual_edits";
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  const versionLine =
    pinnedBlueprintVersion != null && currentBlueprintVersion != null
      ? `Currently pinned to role blueprint v${pinnedBlueprintVersion} · current is v${currentBlueprintVersion}.`
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-assessment-scores-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close update assessment scores dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3
              id="update-assessment-scores-title"
              className="text-lg font-semibold text-text-primary"
            >
              Update assessment scores
            </h3>
            <p className="mt-2 text-sm text-text-muted">
              {manualEdits
                ? "One or more candidates have a manually-edited assessment. Rescoring will " +
                  "overwrite those manual edits with a fresh AI assessment for every candidate on " +
                  "this campaign."
                : "The assessment matrix is out of date for the current role blueprint. " +
                  "Continuing will update the assessment matrix first, then rescore every " +
                  "candidate on this campaign."}
            </p>
            {!manualEdits && versionLine && (
              <p className="mt-2 text-xs text-text-faint">{versionLine}</p>
            )}
            {!manualEdits && matrixLocked && (
              <p className="mt-3 text-sm text-text-muted">
                The assessment matrix is currently locked. The lock will be preserved — it stays
                locked after the update.
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={16} />
          </button>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="confirm-update-assessment-scores"
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {manualEdits
              ? "Overwrite manual edits and rescore"
              : "Update assessment matrix and rescore"}
          </button>
        </div>
      </div>
    </div>
  );
}
